import React from 'react'
import PropTypes from 'prop-types'
import Typography from '@material-ui/core/Typography'
import MuiTableBody from '@material-ui/core/TableBody'
import TableBodyCell from './TableBodyCell'
import TableBodyRow from './TableBodyRow'
import TableSelectCell from './TableSelectCell'
import { withStyles, Box } from '@material-ui/core'
import cloneDeep from 'lodash.clonedeep'
import { getPageValue, warnDeprecated } from '../utils'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'

const defaultBodyStyles = {
  root: {},
  emptyTitle: {
    textAlign: 'center',
  },
  bodyRowTotal: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1000
  },
  groupHeader: {
    backgroundColor: "#f5f5f5",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
    cursor: "pointer",
  },
  groupChild: {
    backgroundColor: "inherit",
  }
}

class TableBody extends React.Component {
  static propTypes = {
    /** Data used to describe table */
    data: PropTypes.array.isRequired,
    /** Total number of data rows */
    count: PropTypes.number.isRequired,
    /** Columns used to describe table */
    columns: PropTypes.array.isRequired,
    /** Options used to describe table */
    options: PropTypes.object.isRequired,
    /** Data used to filter table against */
    filterList: PropTypes.array,
    /** Callback to execute when row is clicked */
    onRowClick: PropTypes.func,
    /** Table rows expanded */
    expandedRows: PropTypes.object,
    /** Table rows selected */
    selectedRows: PropTypes.object,
    /** Callback to trigger table row select */
    selectRowUpdate: PropTypes.func,
    /** The most recent row to have been selected/unselected */
    previousSelectedRow: PropTypes.object,
    /** Data used to search table against */
    searchText: PropTypes.string,
    /** Toggle row expandable */
    toggleExpandRow: PropTypes.func,
    /** Extend the style applied to components */
    classes: PropTypes.object,
  };

  static defaultProps = {
    toggleExpandRow: () => {},
  };

  constructor(props) {
    super(props)
    this.state = {
      groupMap: {},
      processedData: [],
    }
  }

  componentDidMount() {
    if (this.props.options.withGroup) {
      this.processGroupedData()
    }
  }

  componentDidUpdate(prevProps) {
    // Si cambian los datos o la opción de agrupación, reprocesar los datos
    if (
      prevProps.data !== this.props.data ||
      prevProps.options.withGroup !== this.props.options.withGroup ||
      prevProps.options.groupByColumn !== this.props.options.groupByColumn
    ) {
      if (this.props.options.withGroup) {
        this.processGroupedData()
      } else {
        this.setState({ groupMap: {}, processedData: [] })
      }
    }
  }

  processGroupedData = () => {
    const { data, columns, options } = this.props
    // const { groupMap, processedData } = this.state

    if (!options.withGroup) return

    const groupByColumn = options.groupByColumn
    const calculateTotals = options.calculateTotalsGroup || []

    const groupColumnIndex = columns.findIndex((col) =>
      typeof col === "object" ? col.name === groupByColumn : col === groupByColumn,
    )

    if (groupColumnIndex === -1) {
      console.error(`No existe la columna "${groupByColumn}"`, columns)
      return
    }

    const groupMap = {}
    const processedData = []

    if ((!data || data.length <= 0)) return
    
    // Paso 1: Agrupar los datos y calcular totales
    data && data.length > 0 && data.forEach((dataItem, dataIndex) => {
      const row = dataItem.data
      const rowData = Array.isArray(row) ? row : columns.map((col) => row[typeof col === "object" ? col.name : col])

      const groupValue = rowData[groupColumnIndex]

      if (!groupMap[groupValue]) {
        groupMap[groupValue] = {
          rows: [],
          totals: {},
          expanded: false, // Por defecto los grupos están colapsados
        }
      }

      // Agregar la fila al grupo
      groupMap[groupValue].rows.push({
        data: rowData,
        dataIndex: dataItem.dataIndex || dataIndex,
        index: dataIndex,
      })

      // Calcular totales para el grupo
      if (calculateTotals.length > 0) {
        calculateTotals.forEach((totalCol) => {
          const colIndex = columns.findIndex((col) =>
            typeof col === 'object' ? col.name === totalCol : col === totalCol,
          )

          if (colIndex !== -1) {                          
            let value = rowData?.[colIndex] ?? 0

            if (typeof value === 'object') {
                value = value?.props?.value ?? value?.props?.children?.[0] ?? value?.props?.children ?? 0
            }          

            const numValue = Number.parseFloat(value)       
            groupMap[groupValue].totals[totalCol] = ( Number(groupMap[groupValue].totals[totalCol]) || 0 ) + (isNaN(numValue) ? 0 : numValue)
  
          }
        })
      }
    })

    // Paso 2: Crear filas procesadas para la tabla
    Object.keys(groupMap).forEach((groupValue) => {
      // Crear fila de cabecera de grupo
      const groupRow = Array(columns.length).fill('')
      groupRow[groupColumnIndex] = groupValue

      // Agregar totales a la fila de grupo
      calculateTotals.forEach((totalCol) => {
        const colIndex = columns.findIndex((col) =>
          typeof col === 'object' ? col.name === totalCol : col === totalCol,
        )

        if (colIndex !== -1) {
          groupRow[colIndex] = groupMap[groupValue].totals[totalCol]
        }
      })

      // Agregar fila de cabecera de grupo
      processedData.push({
        data: groupRow,
        index: `group-${groupValue}`,
        dataIndex: `group-${groupValue}`,
        isGroupHeader: true,
        groupValue: groupValue,
        expanded: groupMap[groupValue].expanded,
        visible: true
      })

      // Agregar filas del grupo (inicialmente ocultas si el grupo está colapsado)
      groupMap[groupValue].rows.forEach((row) => {
        processedData.push({
          data: row.data,
          index: row.index,
          dataIndex: row.dataIndex,
          isGroupChild: true,
          groupValue: groupValue,
          visible: groupMap[groupValue].expanded
        })
      })
    })

    this.setState({ groupMap, processedData })
  }

  toggleGroup = (groupValue) => {
    const { groupMap, processedData } = this.state

    // Actualizar el estado de expansión del grupo
    const updatedGroupMap = { ...groupMap }
    if (updatedGroupMap && updatedGroupMap[groupValue] && updatedGroupMap[groupValue].expanded) {
      updatedGroupMap[groupValue].expanded = !updatedGroupMap[groupValue].expanded
    }

    // Actualizar la visibilidad de las filas del grupo
    const updatedData = processedData.map((row) => {
      if (row.isGroupChild && row.groupValue === groupValue) {
        return { ...row, visible: updatedGroupMap[groupValue].expanded }
      }
      console.log('row ? ', row)
      return row
    })

    this.setState({ groupMap: updatedGroupMap, processedData: updatedData })
  }

  buildRows() {
    const { data, page, rowsPerPage, count, options, columns } = this.props;
    const { processedData } = this.state

    if (options.withGroup && processedData.length > 0) {
      return processedData
    }

    if (options.serverSide) return data.length ? data : null

    let rows = []
    const highestPageInRange = getPageValue(count, rowsPerPage, page)
    const fromIndex = highestPageInRange === 0 ? 0 : highestPageInRange * rowsPerPage
    const toIndex = Math.min(count, (highestPageInRange + 1) * rowsPerPage)

    if (page > highestPageInRange) {
      console.warn("Current page is out of range, using the highest page that is in range instead.");
    }

    for (let rowIndex = fromIndex; rowIndex < count && rowIndex < toIndex; rowIndex++) {
      if (data[rowIndex] !== undefined) {
        let rowData;
        let rowIndexProp;

        // Verificar si 'data' es un array y 'dataIndex' es un numero
        if (Array.isArray(data[rowIndex]?.data) && typeof data[rowIndex]?.dataIndex === 'number') {
          rowData = data[rowIndex].data;
          rowIndexProp = data[rowIndex].dataIndex
        } else {
          rowData = data[rowIndex]
          rowIndexProp = rowIndex
        }

        rows.push({
          data: Array.isArray(rowData)
            ? rowData
            : columns.map((col) => rowData[typeof col === 'object' ? col.name : col]),
          dataIndex: rowIndexProp,
        })
      }
    }

    return rows.length ? rows : null
  }

  getRowIndex(index) {
    const { page, rowsPerPage, options } = this.props;

    if (options.serverSide) {
      return index;
    }

    const startIndex = page === 0 ? 0 : page * rowsPerPage;
    return startIndex + index;
  }

  isRowSelected(dataIndex) {
    const { selectedRows } = this.props;
    return selectedRows.lookup && selectedRows.lookup[dataIndex] ? true : false;
  }

  isRowExpanded(dataIndex) {
    const { expandedRows } = this.props;
    return expandedRows.lookup && expandedRows.lookup[dataIndex] ? true : false;
  }

  isRowSelectable(dataIndex, selectedRows) {
    const { options } = this.props;
    selectedRows = selectedRows || this.props.selectedRows;

    if (options.isRowSelectable) {
      return options.isRowSelectable(dataIndex, selectedRows);
    } else {
      return true;
    }
  }

  isRowExpandable(dataIndex) {
    const { options, expandedRows } = this.props;
    if (options.isRowExpandable) {
      return options.isRowExpandable(dataIndex, expandedRows);
    } else {
      return true;
    }
  }

  handleRowSelect = (data, event) => {
    let shiftKey = event && event.nativeEvent ? event.nativeEvent.shiftKey : false;
    let shiftAdjacentRows = [];
    let previousSelectedRow = this.props.previousSelectedRow;

    // If the user is pressing shift and has previously clicked another row.
    if (shiftKey && previousSelectedRow && previousSelectedRow.index < this.props.data.length) {
      let curIndex = previousSelectedRow.index;

      // Create a copy of the selectedRows object. This will be used and modified
      // below when we see if we can add adjacent rows.
      let selectedRows = cloneDeep(this.props.selectedRows);

      // Add the clicked on row to our copy of selectedRows (if it isn't already present).
      let clickedDataIndex = this.props.data[data.index].dataIndex;
      if (selectedRows.data.filter(d => d.dataIndex === clickedDataIndex).length === 0) {
        selectedRows.data.push({
          index: data.index,
          dataIndex: clickedDataIndex,
        });
        selectedRows.lookup[clickedDataIndex] = true;
      }

      while (curIndex !== data.index) {
        let dataIndex = this.props.data[curIndex].dataIndex;

        if (this.isRowSelectable(dataIndex, selectedRows)) {
          let lookup = {
            index: curIndex,
            dataIndex: dataIndex,
          };

          // Add adjacent row to temp selectedRow object if it isn't present.
          if (selectedRows.data.filter(d => d.dataIndex === dataIndex).length === 0) {
            selectedRows.data.push(lookup);
            selectedRows.lookup[dataIndex] = true;
          }

          shiftAdjacentRows.push(lookup);
        }
        curIndex = data.index > curIndex ? curIndex + 1 : curIndex - 1;
      }
    }
    this.props.selectRowUpdate('cell', data, shiftAdjacentRows);
  };

  
  handleRowClick = (row, data, event) => {

    // Don't trigger onRowClick if the event was actually the expandable icon.
    if (
      event.target.id === 'expandable-button' ||
      (event.target.nodeName === 'path' && event.target.parentNode.id === 'expandable-button')
    ) {
      // In a future release, onRowClick will no longer be called here (for consistency).
      // For now, issue a deprecated warning.
      if (this.props.options.onRowClick) {
        warnDeprecated(
          'Clicks on expandable button will not trigger onRowClick in an upcoming release, see: https://github.com/gregnb/mui-datatables/issues/516.',
        );
        this.props.options.onRowClick(row, data, event);
      }

      return;
    }

    // Don't trigger onRowClick if the event was actually a row selection via checkbox
    if (event.target.id && event.target.id.startsWith('MUIDataTableSelectCell')) return;

    // Check if we should toggle row select when row is clicked anywhere
    if (
      this.props.options.selectableRowsOnClick &&
      this.props.options.selectableRows !== 'none' &&
      this.isRowSelectable(data.dataIndex, this.props.selectedRows)
    ) {
      const selectRow = { index: data.rowIndex, dataIndex: data.dataIndex };
      this.handleRowSelect(selectRow, event);
    }
    // Check if we should trigger row expand when row is clicked anywhere
    if (
      this.props.options.expandableRowsOnClick &&
      this.props.options.expandableRows &&
      this.isRowExpandable(data.dataIndex, this.props.expandedRows)
    ) {
      const expandRow = { index: data.rowIndex, dataIndex: data.dataIndex };
      this.props.toggleExpandRow(expandRow);
    }

    // Don't trigger onRowClick if the event was actually a row selection via click
    if (this.props.options.selectableRowsOnClick) return;

    this.props.options.onRowClick && this.props.options.onRowClick(row, data, event);
  };

  render() {
    const { classes, columns, toggleExpandRow, options, generarTotalRow, data } = this.props
    const { groupMap } = this.state

    const tableRows = this.buildRows()
      //   const rows = this.buildRows()
    const visibleColCnt = columns.filter(c => c.display === 'true').length
   
    let totalRow = generarTotalRow ? generarTotalRow(data) : []
    // console.log('groupMap : ', groupMap)
    
    return (
      <>   
       <MuiTableBody>                
        {tableRows && tableRows.length > 0 
          ? (
            tableRows.map((data, rowIndex) => {
            const { data: row, dataIndex } = data
          
              //             rows.map((data, dataIndex) => {                 
            const isGroupHeader = options.withGroup && data.isGroupHeader
            const isGroupChild = options.withGroup && data.isGroupChild
            const isVisible = !isGroupChild || data.visible
            const groupValue = data.groupValue
            
            if (isGroupChild && !isVisible) return null                                                         

            if (options.customRowRender) {
              return options.customRowRender(row, dataIndex, rowIndex)
            }
  
            return (
              <React.Fragment key={rowIndex}>
                <TableBodyRow
                  {...(options.setRowProps ? options.setRowProps(row, dataIndex) : {})}
                  options={options}
                  rowSelected={options.selectableRows !== 'none' ? this.isRowSelected(dataIndex) : false}
                //  rowSelected={
                //   options.selectableRows !== "none" && !isGroupHeader ? this.isRowSelected(rowIndex) : false
                //  }
                  onClick={ isGroupHeader
                    ? this.toggleGroup.bind(groupValue)
                    : this.handleRowClick.bind(null, row, { rowIndex, dataIndex })
                  }
                  data-testid={'MUIDataTableBodyRow-' + dataIndex}
                  className={isGroupHeader ? classes.groupHeader : isGroupChild ? classes.groupChild : ''}
                  id={'MUIDataTableBodyRow-' + dataIndex}
                >

                {/* Celda de selección (checkbox) */}

           {/* {!isGroupHeader && */}
                  <TableSelectCell
                    onChange={this.handleRowSelect.bind(null, {
                      index: this.getRowIndex(rowIndex),
                      dataIndex: dataIndex,
                    })}
                    onExpand={toggleExpandRow.bind(null, {
                      index: this.getRowIndex(rowIndex),
                      dataIndex: dataIndex,
                    })}
                    fixedHeader={options.fixedHeader}
                    fixedHeaderOptions={options.fixedHeaderOptions}
                    checked={this.isRowSelected(dataIndex)}
                    expandableOn={options.expandableRows}
                    // When rows are expandable, but this particular row isn't expandable, set this to true.
                    // This will add a new class to the toggle button, MUIDataTableSelectCell-expandDisabled.
                    hideExpandButton={!this.isRowExpandable(dataIndex) && options.expandableRows}
                    selectableOn={options.selectableRows}
                    isRowExpanded={this.isRowExpanded(dataIndex)}
                    isRowSelectable={this.isRowSelectable(dataIndex)}
                    id={'MUIDataTableSelectCell-' + dataIndex}
                  />

                  {/* Celdas normales para filas no agrupadas o filas hijas */}                  
                  { !isGroupHeader &&                  
                    (row.map(
                      (column, columnIndex) =>
                        columns[columnIndex].display === 'true' && (
                          <TableBodyCell
                            {...(columns[columnIndex].setCellProps
                              ? columns[columnIndex].setCellProps(column, dataIndex, columnIndex)
                              : {})}
                            data-testid={`MuiDataTableBodyCell-${columnIndex}-${rowIndex}`}
                            dataIndex={dataIndex}
                            rowIndex={rowIndex}
                            colIndex={columnIndex}
                            columnHeader={columns[columnIndex].label}
                            print={columns[columnIndex].print}
                            options={options}
                            key={columnIndex}
                          >
                            {column}
                          </TableBodyCell>
                        ),
                    ))
                  }

                  {/* Si es una fila de cabecera de grupo, mostrar una celda que ocupe todo el ancho */}
                  { isGroupHeader &&   
                    ( <TableBodyCell
                        colSpan={options.selectableRows !== 'none' ? visibleColCnt + 1 : visibleColCnt}
                        options={options}
                        colIndex={0}
                        rowIndex={rowIndex}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {(groupValue && groupMap && groupMap[groupValue]?.expanded)
                            ? <KeyboardArrowDownIcon fontSize='small' />
                            : ((groupValue && groupMap)
                                ? <KeyboardArrowRightIcon fontSize='small' />
                                : null
                              )
                          }

                            <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>
                               {columns.find((col) =>
                                 typeof col === 'object'
                                   ? col.name === options.groupByColumn
                                   : col === options.groupByColumn,
                               )?.label || options.groupByColumn}
                               : {groupValue}
                            </span>   

                            {options.calculateTotalsGroup && options.calculateTotalsGroup.length > 0 && groupValue && groupMap[groupValue]?.totals && (
                              <Box sx={{ display: 'flex', marginLeft: 'auto' }}>
                                {options.calculateTotalsGroup.map((totalCol, idx) => {
                                  const colIndex = columns.findIndex((col) =>
                                    typeof col === 'object' ? col.name === totalCol : col === totalCol,
                                  )

                                  if (colIndex === -1) return null

                                  console.log(groupMap, groupMap[groupValue], ' total? ', groupMap[groupValue].totals)
                                  const colLabel = columns[colIndex].label || totalCol
                                  const totalValue = groupMap[groupValue]?.totals[totalCol]

                                  return (
                                     <Box key={idx} sx={{ marginLeft: '16px', color: 'primary.main' }}>
                                       <span style={{ fontWeight: 'normal' }}>{colLabel}: </span>
                                       <span style={{ fontWeight: 'bold' }}>
                                         {columns[colIndex]?.options?.customBodyRender
                                           ? columns[colIndex].options.customBodyRender(totalValue)
                                           : totalValue}
                                       </span>
                                    </Box>
                                  )                                    
                                })}
                              </Box>
                            )}                    
                         </Box>              
                      </TableBodyCell>
                    )
                  }      
                </TableBodyRow>            
                {/* {this.isRowExpanded(dataIndex) && options.renderExpandableRow(row, { rowIndex, dataIndex })}       */}
                {/* Fila expandible, si esta habilitada  */}
                {!isGroupHeader && this.isRowExpanded(dataIndex) && options.renderExpandableRow(row, { rowIndex, dataIndex })}           
            </React.Fragment>              
            );
        })    
        ) : (
            <TableBodyRow options={options}>
              <TableBodyCell
                colSpan={options.selectableRows !== 'none' || options.expandableRows ? visibleColCnt + 1 : visibleColCnt}
                options={options}
                colIndex={0}
                rowIndex={0}>
                <Typography variant="subtitle1" className={classes.emptyTitle}>
                  {options.textLabels.body.noMatch}
                </Typography>
              </TableBodyCell>
            </TableBodyRow>
          )              
        }
        
        {/* Fila de totales */}
        {data && data.length > 0 
          ?
            <TableBodyRow
              options={options}  
              total={true}
              className={classes.bodyRowTotal}         
            >
              {totalRow && (totalRow.length > 0) && totalRow.map(
                  (total, totalIndex) =>
                    <TableBodyCell                   
                        colIndex={totalIndex}                 
                        options={options}
                        key={totalIndex}                      
                        style={{ 
                          color: total.totalColor, 
                          fontSize:'0.95rem', 
                          fontWeight: 'bold', 
                          padding:'5px',
                          position: (total.sticky || total.stickyRight ) ? 'sticky' : 'inherit',
                          zIndex: (total.sticky  || total.stickyRight ) ? '10000' : 'inherit',
                          left: total.sticky ? 0 : 'inherit',
                          right: total.stickyRight ? 0 : 'inherit',
                          textAlign: total.totalFloat !== 'inherit' ? total.totalFloat : 'left',
                          backgroundColor: total.totalBackground
                        }}   
                      >
                        { total.total }
                    </TableBodyCell>
                  )                                       
                }  
              </TableBodyRow>       
          : null 
        } 
      </MuiTableBody>
    </>     
    );
  }
}

export default withStyles(defaultBodyStyles, { name: 'MUIDataTableBody' })(TableBody);
